import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { applyActionCode } from 'firebase/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { sendVerificationEmail } from '@/lib/callables'
import { auth } from '@/lib/firebase'

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const oobCode = useMemo(() => searchParams.get('oobCode'), [searchParams])
  const next = searchParams.get('next')
  const { user, isGuest } = useAuth()

  const [status, setStatus] = useState<'idle' | 'applying' | 'success' | 'error'>(
    oobCode ? 'applying' : 'idle',
  )
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!oobCode) return
    let cancelled = false
    ;(async () => {
      setStatus('applying')
      try {
        await applyActionCode(auth, oobCode)
        if (!cancelled) {
          setStatus('success')
          setMessage('Your email has been verified.')
          if (auth.currentUser) await auth.currentUser.reload()
        }
      } catch (e) {
        if (!cancelled) {
          setStatus('error')
          setMessage(e instanceof Error ? e.message : 'Verification link is invalid or expired.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [oobCode])

  async function resend() {
    if (!user || isGuest) return
    setBusy(true)
    setMessage(null)
    try {
      await sendVerificationEmail()
      setMessage('Verification email sent. Check your inbox.')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to send verification email')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gradient-to-b from-navy to-navy-light px-4 py-10">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center">
          <CardTitle>Verify email</CardTitle>
          <CardDescription>
            {status === 'applying'
              ? 'Confirming your email…'
              : 'Confirm your email address to unlock ownership claims and more.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'applying' ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : null}

          {message ? (
            <p className={`text-sm ${status === 'error' ? 'text-danger' : 'text-success'}`}>
              {message}
            </p>
          ) : null}

          {status === 'success' ? (
            <Button asChild variant="primary" className="w-full">
              <Link to={next || '/search'}>Continue</Link>
            </Button>
          ) : null}

          {status !== 'applying' && status !== 'success' ? (
            <>
              {user && !isGuest ? (
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={busy || user.emailVerified}
                  onClick={() => void resend()}
                >
                  {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                  {user.emailVerified ? 'Email already verified' : 'Resend verification email'}
                </Button>
              ) : (
                <Button asChild variant="primary" className="w-full">
                  <Link to={`/signin?next=${encodeURIComponent(next || '/verify-email')}`}>
                    Sign in to resend
                  </Link>
                </Button>
              )}
              <Button asChild variant="ghost" className="w-full">
                <Link to={next || '/settings'}>Back</Link>
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
